<template>
  <v-container>
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-card elevation="2">
          <v-card-title>Progress</v-card-title>
          <div v-if="loading">
            <v-row>
              <v-col cols="11" align="center" justify="center">
                <v-row align="center" justify="center">
                  <v-text-field color="success" loading disabled></v-text-field>
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div v-if="!loading">
            <strong v-if="!showAll">Over the last {{ clientProgress.weeks }} weeks:</strong>
            <strong v-else>Over the last 4 weeks:</strong>
            <v-card-text>
              <v-row @click="showTrainingLog()">
                <v-col cols="4" align="center" class="pa-0">
                  <h2 class="grey-navy--text">Exercise</h2>
                  <v-avatar color="exercise" class="mt-2" size="60">
                    <span class="white--text headline">{{ clientProgress.exerciseDots || 0 }}</span>
                  </v-avatar>
                </v-col>
                <v-col cols="4" align="center" class="pa-0">
                  <h2 class="grey-navy--text">Nutrition</h2>
                  <v-avatar color="nutrition" class="mt-2" size="60">
                    <span class="white--text headline">{{ clientProgress.nutritionDots || 0 }}</span>
                  </v-avatar>
                </v-col>
                <v-col cols="4" align="center" class="pa-0">
                  <h2 class="grey-navy--text">Mindset</h2>
                  <v-avatar color="mindset" class="mt-2" size="60">
                    <span class="white--text headline">{{ clientProgress.mindsetDots || 0 }}</span>
                  </v-avatar>
                </v-col>
              </v-row>

              <v-row class="pt-3">
                <v-col cols="5" class="my-auto">
                  <div v-if="clientProgress.ratingFirst || clientProgress.nutritionRatingFirst">
                    <ratings v-if="clientProgress.ratingFirst" :rating="clientProgress.ratingFirst" />
                    <ratings
                      v-if="clientProgress.nutritionRatingFirst"
                      :rating="clientProgress.nutritionRatingFirst"
                      icon="mdi-food-apple"
                      color="green"
                    />
                  </div>
                  <span v-else>No ratings</span>
                </v-col>
                <v-col cols="2" class="my-auto">
                  <v-icon color="blue">mdi-arrow-right</v-icon>
                </v-col>
                <v-col cols="5">
                  <div v-if="clientProgress.ratingLast || clientProgress.nutritionRatingLast">
                    <ratings v-if="clientProgress.ratingLast" :rating="clientProgress.ratingLast" />
                    <ratings
                      v-if="clientProgress.nutritionRatingLast"
                      :rating="clientProgress.nutritionRatingLast"
                      icon="mdi-food-apple"
                      color="green"
                    />
                  </div>
                  <span v-else>No ratings</span>
                </v-col>
              </v-row>
            </v-card-text>
          </div>
          <div v-if="noAssessment" class="ma-6">
            <hr />
            <span class="ma-3">Not enough check-ins to compare losses yet</span>
            <hr />
          </div>
          <div v-if="!loading && !noAssessment">
            <v-simple-table class="my-2">
              <tbody>
                <tr>
                  <td></td>
                  <td>
                    <h3>Total change</h3>
                  </td>
                  <td>
                    <h3>Total change per week</h3>
                  </td>
                  <td />
                  <td>
                    <h3>Total change</h3>
                  </td>
                  <td>
                    <h3>Total change per week</h3>
                  </td>
                </tr>
                <tr>
                  <td><strong>Weight</strong></td>
                  <td>{{ clientProgress.weight | formatDecimals }} kg</td>
                  <td>{{ clientProgress.weeklyWeight | formatDecimals }} kg</td>
                  <td><strong>1 mile</strong></td>
                  <td>{{ clientProgress.oneMile | humanizeSeconds }}</td>
                  <td>{{ clientProgress.weeklyOneMile | humanizeSeconds }}</td>
                </tr>
                <tr>
                  <td><strong>Chest</strong></td>
                  <td>{{ clientProgress.chest | formatDecimals }} cm</td>
                  <td>{{ clientProgress.weeklyChest | formatDecimals }} cm</td>
                  <td><strong>Push-ups</strong></td>
                  <td>{{ clientProgress.pushUps }}</td>
                  <td>{{ clientProgress.weeklyPushUps | formatDecimals }}</td>
                </tr>
                <tr>
                  <td><strong>Abdomen</strong></td>
                  <td>{{ clientProgress.abdomen | formatDecimals }} cm</td>
                  <td>{{ clientProgress.weeklyAbdomen | formatDecimals }} cm</td>
                  <td><strong>Sit-ups</strong></td>
                  <td>{{ clientProgress.sitUps }}</td>
                  <td>{{ clientProgress.weeklySitUps | formatDecimals }}</td>
                </tr>
                <tr>
                  <td><strong>Hip</strong></td>
                  <td>{{ clientProgress.hip | formatDecimals }} cm</td>
                  <td>{{ clientProgress.weeklyHip | formatDecimals }} cm</td>
                  <td><strong>Burpee / Chirpee</strong></td>
                  <td>{{ clientProgress.burpeeChirpee }}</td>
                  <td>{{ clientProgress.weeklyBurpeeChirpee | formatDecimals }}</td>
                </tr>
                <tr>
                  <td><strong>Quad</strong></td>
                  <td>{{ clientProgress.quad | formatDecimals }} cm</td>
                  <td>{{ clientProgress.weeklyQuad | formatDecimals }} cm</td>
                  <td><strong>Elbow Plank</strong></td>
                  <td>{{ clientProgress.elbowPlank }}</td>
                  <td>{{ clientProgress.weeklyElbowPlank | formatDecimals }}</td>
                </tr>
                <tr>
                  <td><h3>Total cm</h3></td>
                  <td>
                    <strong>{{ clientProgress.totalCm | formatDecimals }} cm</strong>
                  </td>
                  <td>
                    <strong>{{ clientProgress.weeklyTotalCm | formatDecimals }} cm</strong>
                  </td>
                  <td><strong>Squat / Wall Squat</strong></td>
                  <td>{{ clientProgress.squat }}</td>
                  <td>{{ clientProgress.weeklySquat | formatDecimals }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <div v-if="!loading">
            <v-row>
              <v-col cols="4">
                <h4>Front</h4>
              </v-col>
              <v-col cols="4">
                <h4>Back</h4>
              </v-col>
              <v-col cols="4">
                <h4>Side</h4>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-row @click="openImage('Front')">
                  <v-col cols="6" class="pl-2">
                    <ProgressImageBlock :image="clientProgress.frontImgFirst" :firstAssessment="true">
                    </ProgressImageBlock>
                  </v-col>
                  <v-col cols="6" class="pr-2">
                    <ProgressImageBlock :image="clientProgress.frontImgLast" :firstAssessment="false">
                    </ProgressImageBlock>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.frontImgFirst"
                      small
                      @click="download(clientProgress.frontImgFirst, `${clientProgress.username}-front-first`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.frontImgLast"
                      small
                      @click="download(clientProgress.frontImgLast, `${clientProgress.username}-front-last`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="4">
                <v-row @click="openImage('Back')">
                  <v-col cols="6" class="pl-2">
                    <ProgressImageBlock :image="clientProgress.backImgFirst" :firstAssessment="true">
                    </ProgressImageBlock>
                  </v-col>
                  <v-col cols="6" class="pr-2">
                    <ProgressImageBlock :image="clientProgress.backImgLast" :firstAssessment="false">
                    </ProgressImageBlock>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.backImgFirst"
                      small
                      @click="download(clientProgress.backImgFirst, `${clientProgress.username}-back-first`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.backImgLast"
                      small
                      @click="download(clientProgress.backImgLast, `${clientProgress.username}-back-last`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="4">
                <v-row @click="openImage('Side')">
                  <v-col cols="6" class="pl-2">
                    <ProgressImageBlock :image="clientProgress.sideImgFirst" :firstAssessment="true">
                    </ProgressImageBlock>
                  </v-col>
                  <v-col cols="6" class="pr-2">
                    <ProgressImageBlock :image="clientProgress.sideImgLast" :firstAssessment="false">
                    </ProgressImageBlock>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.sideImgFirst"
                      small
                      @click="download(clientProgress.sideImgFirst, `${clientProgress.username}-side-first`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                  <v-col cols="6">
                    <v-icon
                      v-if="clientProgress.sideImgLast"
                      small
                      @click="download(clientProgress.sideImgLast, `${clientProgress.username}-side-last`)"
                      >save_alt</v-icon
                    >
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            Click on images for better comparison
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="viewAllAssessments">View all check-ins</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="modals.trainingLog" transition="dialog-bottom-transition">
      <v-card>
        <WeeklyTrainingLog :userId="userId" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="modals.progressImage.show" transition="dialog-bottom-transition" max-width="80%">
      <ClientProgressPhoto
        :title="modals.progressImage.title"
        :firstImage="modals.progressImage.firstImage"
        :lastImage="modals.progressImage.lastImage"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { CLIENT_PROGRESS_QUERY } from "@/graphql/queries/user";
import ClientProgressPhoto from "./progress/ClientProgressPhoto";
import ProgressImageBlock from "./progress/ProgressImageBlock";
import WeeklyTrainingLog from "@/components/training_log/WeeklyTrainingLog";
import FileDownloadMixin from "@/components/shared/mixins/FileDownloadMixin";
import Ratings from "@/components/shared/Ratings";

export default {
  name: "ClientProgress",
  mixins: [FileDownloadMixin],
  props: ["coachId", "userId", "showAll"],
  components: {
    ClientProgressPhoto,
    ProgressImageBlock,
    WeeklyTrainingLog,
    Ratings,
  },
  data() {
    return {
      loading: true,
      noAssessment: false,
      clientProgress: {
        username: "",
      },
      modals: {
        progressImage: {
          show: false,
          title: "",
          firstImage: null,
          lastImage: null,
        },
        trainingLog: false,
      },
    };
  },
  filters: {
    formatDecimals(value) {
      let rounded = (Math.round((value + Number.EPSILON) * 100) / 100).toString();

      //show the loss or gain, which is inversion of the difference
      // if negative difference, invert and explicitly show +
      // if positive difference explicitly show -
      return rounded[0] == "-" ? `+ ${rounded * -1}` : `- ${rounded}`;
    },
  },
  methods: {
    openImage(view) {
      this.modals.progressImage.show = true;
      this.modals.progressImage.title = view;

      switch (view) {
        case "Front":
          this.modals.progressImage.firstImage = this.clientProgress.frontImgFirst;
          this.modals.progressImage.lastImage = this.clientProgress.frontImgLast;
          break;
        case "Back":
          this.modals.progressImage.firstImage = this.clientProgress.backImgFirst;
          this.modals.progressImage.lastImage = this.clientProgress.backImgLast;
          break;
        case "Side":
          this.modals.progressImage.firstImage = this.clientProgress.sideImgFirst;
          this.modals.progressImage.lastImage = this.clientProgress.sideImgLast;
          break;
      }
    },
    showTrainingLog() {
      this.modals.trainingLog = true;
    },
    viewAllAssessments() {
      let params = { userId: this.userId };
      if (this.coachId) {
        params.coachId = this.coachid;
      }
      return this.$router.push({ name: "CoachClientAssessments", params: params });
    },
  },
  apollo: {
    clientProgress: {
      query: CLIENT_PROGRESS_QUERY,
      variables() {
        return {
          userId: this.userId,
          showAll: this.showAll,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;

          if (!response.data.clientProgress.weeks) {
            this.noAssessment = true;
          }
        }
      },
    },
  },
};
</script>

<style scoped>
div.col.col-4 .col-6 {
  padding: 0;
}

div.col.col-4 .col-6:hover {
  cursor: zoom-in;
}

.v-card__text {
  cursor: pointer;
}

.row:last-child {
  margin: 0;
}
</style>
